import { observable, computed, action } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import type {
  ArchiveDocumentFn,
  ConfirmDocumentFn,
  DownloadDocumentFn,
} from '@/features/documents'
import type { Document } from '@/features/documents/Document'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'

/**
 * Asks the user to review a document that needs manual verification, then
 * confirm or delete it.
 */
export class DocumentManualVerificationModalViewModel {
  /**
   * View model for the underlying modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * The document being verified.
   */
  @observable accessor document: Document | null = null

  /**
   * The job the document belongs to.
   */
  @observable accessor job: Job | null = null

  /**
   * Whether the user opened the document before deciding.
   */
  @observable accessor hasViewedDocument = false

  constructor(
    private readonly _downloadDocument: DownloadDocumentFn,
    private readonly _confirmDocument: ConfirmDocumentFn,
    private readonly _archiveDocument: ArchiveDocumentFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Whether the document only lacks the Pro's signature.
   */
  @computed
  get isMissingProSignature() {
    return (
      this.document?.stage.type === 'ManualAttestationRequired' &&
      this.document?.stage.reason === 'MissingProSignature'
    )
  }

  /**
   * Shows the modal for a document of a job.
   */
  @action.bound
  open(document: Document, job: Job) {
    this.document = document
    this.job = job
    this.hasViewedDocument = false
    this.modal.show()
  }

  /**
   * Opens the document for review.
   */
  @action.bound
  viewDocument() {
    if (!this.document) {
      return
    }
    this.hasViewedDocument = true
    void Promise.resolve(this._downloadDocument(this.document, true))
      .catch(this._createAlertErrorHandler('Opening the document failed'))
      .catch(() => undefined)
  }

  /**
   * Confirms the document as verified.
   */
  @action.bound
  async confirmDocument() {
    if (!this.document?.isManualAttestationRequired) {
      return
    }
    try {
      await this._confirmDocument(this.document.id, this.job?.id).catch(
        this._createAlertErrorHandler('Confirming the document failed'),
      )
    } finally {
      this.modal.close()
    }
  }

  /**
   * Deletes the document.
   */
  @action.bound
  async deleteDocument() {
    if (!this.document) {
      return
    }

    const docTitle = this.document.title
    try {
      await this._archiveDocument(this.document).catch(
        this._createAlertErrorHandler('Deleting the document failed'),
      )
    } finally {
      this.modal.close()
    }

    this._newAlert()
      .withState('success')
      .saying(`Success!  Document ${docTitle} has been removed!`)
      .autoDismiss()
      .show()
  }
}
